import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { errMsg } from "../lib/api";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState(loc.state?.email || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.full_name}`);
      const dest = u.role === "admin" || u.role === "recruiter" ? "/admin"
        : u.role === "employer" ? "/employer" : "/candidate";
      nav(loc.state?.from || dest);
    } catch (e) {
      toast.error(errMsg(e));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-5rem)] flex items-center justify-center bg-[#F8FAFC] py-16" data-testid="login-page">
      <div className="w-full max-w-md ae-card p-8">
        <div className="ae-overline mb-2">Welcome back</div>
        <h1 className="font-heading text-3xl font-medium text-[#0F2A4A] mb-6">Sign in</h1>

        <GoogleSignInButton label="Sign in with Google" />

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200"></div></div>
          <div className="relative flex justify-center text-xs"><span className="bg-white px-3 text-slate-500 uppercase tracking-wider">or with email</span></div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <input required type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)}
            data-testid="login-email" className="w-full rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#C5A059] outline-none px-4 py-3 text-sm" />
          <input required type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)}
            data-testid="login-password" className="w-full rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#C5A059] outline-none px-4 py-3 text-sm" />
          <div className="text-right -mt-2">
            <Link to="/forgot-password" className="text-xs text-[#C5A059] font-medium hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
          </div>
          <button disabled={loading} className="ae-btn-primary w-full" data-testid="login-submit">
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>
        <div className="mt-6 text-center text-sm text-slate-600">
          No account? <Link to="/register" className="text-[#C5A059] font-medium">Create one</Link>
        </div>
      </div>
    </div>
  );
}

export function GoogleSignInButton({ label = "Continue with Google" }) {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleClick = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button onClick={handleClick} type="button" data-testid="google-signin-btn"
      className="w-full flex items-center justify-center gap-3 border-2 border-slate-200 hover:border-[#C5A059] rounded-xl px-4 py-3 font-medium text-sm text-[#0F2A4A] transition-all hover:shadow-md">
      <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
      </svg>
      {label}
    </button>
  );
}
